import { Bars3Icon, HeartIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Link, useLocation } from 'react-router-dom';
import React, { useEffect, useState } from 'react';

import { NavItem } from '../types';

const Header: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const navigation: NavItem[] = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Contact', href: '/contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isCurrentPage = (href: string) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'glass-effect shadow-2xl' 
          : 'bg-transparent'
      }`}
    >
      <nav className="container-custom">
        <div className="flex items-center justify-between h-20">
          {/* Logo with Heart Icon */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="relative">
                <div className="absolute inset-0 transition-opacity duration-300 rounded-full opacity-50 bg-coral-500 blur-sm group-hover:opacity-75"></div>
                <div className="relative p-2 bg-white rounded-full shadow-lg">
                  <HeartIcon className="w-8 h-8 text-coral-500" />
                </div>
              </div>
              <div>
                <h1 className="text-2xl font-bold lg:text-3xl font-heading text-gradient">
                  Blessed Gideon
                </h1>
                <p className="-mt-1 text-sm font-body text-navy-600">Charity</p>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div className="flex items-center space-x-2">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`relative px-6 py-3 rounded-full font-medium transition-all duration-300 transform hover:scale-105 ${
                    isCurrentPage(item.href)
                      ? 'bg-navy-800 text-white shadow-lg'
                      : 'text-navy-700 hover:bg-navy-50'
                  }`}
                >
                  <span className="relative z-10">{item.name}</span>
                  {isCurrentPage(item.href) && (
                    <div className="absolute inset-0 rounded-full bg-navy-800 animate-pulse"></div>
                  )}
                </Link>
              ))}
             
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              type="button"
              className={`relative p-3 rounded-full transition-all duration-300 ${
                mobileMenuOpen 
                  ? 'bg-coral-500 text-white' 
                  : 'bg-white/80 text-navy-800 hover:bg-white'
              }`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <span className="sr-only">Toggle menu</span>
              <div className="relative w-6 h-6">
                <Bars3Icon 
                  className={`absolute inset-0 w-6 h-6 transition-all duration-300 ${
                    mobileMenuOpen ? 'opacity-0 rotate-180' : 'opacity-100 rotate-0'
                  }`}
                />
                <XMarkIcon 
                  className={`absolute inset-0 w-6 h-6 transition-all duration-300 ${
                    mobileMenuOpen ? 'opacity-100 rotate-0' : 'opacity-0 -rotate-180'
                  }`}
                />
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div 
          className={`md:hidden transition-all duration-300 overflow-hidden ${
            mobileMenuOpen 
              ? 'max-h-96 opacity-100 pb-6' 
              : 'max-h-0 opacity-0'
          }`}
        >
          <div className="p-6 mt-4 space-y-4 glass-effect rounded-2xl">
            {navigation.map((item, index) => (
              <Link
                key={item.name}
                to={item.href}
                className={`block py-3 px-6 rounded-xl font-medium transition-all duration-300 transform hover:scale-105 ${
                  isCurrentPage(item.href)
                    ? 'bg-navy-800 text-white'
                    : 'text-navy-700 hover:bg-navy-50'
                } ${index === 0 ? 'animate-slide-up' : index === 1 ? 'animate-slide-up delay-100' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}
            <button 
              className="w-full delay-200 btn-coral animate-slide-up"
            >
              Donate Now
            </button>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
